import { DateTimeFormats, formatDateTime } from '@bondsports/date-time';
import { AnswerDto } from '@bondsports/types';
import { RCAddress } from '../../types/entities/coreEntites/Address';
import { RCCustomQuestionType, RCQuestionTypesEnum } from '../../types/entities/coreEntites/Questions';
import { getAddressString } from './address';

const DASH = '-';
const YES = 'Yes';
const NO = 'No';
const AGREED = 'Agreed';
const DEFAULT_LIST_SEPARATOR = ', ';
const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'webp']);
const ALLOWED_IMAGE_HOST_SUFFIX = '.cloudinary.com';
const CLOUDINARY_REMOTE_FETCH_PATH = /\/(?:image|video|raw)\/fetch(?:\/|$)/i;
const CLOUDINARY_FETCH_TRANSFORM = /(?:^|[/_ ,])(?:l_|u_)?fetch:/i;
const CONSENT_QUESTION_TYPES = new Set<string>([RCQuestionTypesEnum.WAIVER, RCQuestionTypesEnum.CUSTOM_WAIVER]);

export interface IAnswerMetadata {
	consentText?: string | null;
	customType?: RCCustomQuestionType | string;
	listSeparator?: string;
}

export interface IAnswerTextSource {
	answerValue: unknown;
	questionId?: number;
	questionType?: string;
}

export interface IFileRef {
	fileName: string;
	url: string;
}

/**
 * Unwraps nested `{ value }` answer payloads used by some question types.
 * @param answerValue - Raw answer value
 * @returns The unwrapped value
 */
export function unwrapAnswerValue(answerValue: unknown): unknown {
	if (
		answerValue !== null &&
		typeof answerValue === 'object' &&
		!Array.isArray(answerValue) &&
		'value' in answerValue
	) {
		return (answerValue as { value: unknown }).value;
	}

	return answerValue;
}

/**
 * @param value - Candidate URL
 * @returns True when the string is an http(s) URL
 */
function isHttpUrl(value: string): boolean {
	return /^https?:\/\//i.test(value);
}

/**
 * @param url - File URL
 * @returns Last path segment, or the raw URL when parsing fails
 */
function fileNameFromUrl(url: string): string {
	try {
		const pathname = new URL(url).pathname;
		return decodeURIComponent(pathname.split('/').filter(Boolean).pop() ?? '') || url;
	} catch {
		return url.split('/').filter(Boolean).pop() ?? url;
	}
}

/**
 * Reads a Cloudinary or generic file URL and filename from known upload shapes.
 * @param answerValue - Raw or unwrapped answer value
 * @returns File reference, or null when the value is not a file
 */
function extractFileRef(answerValue: unknown): IFileRef | null {
	const value = unwrapAnswerValue(answerValue);

	if (typeof value === 'string' && isHttpUrl(value)) {
		return { url: value, fileName: fileNameFromUrl(value) };
	}

	if (value === null || typeof value !== 'object' || Array.isArray(value)) {
		return null;
	}

	const record = value as Record<string, unknown>;
	const url =
		(typeof record.secure_url === 'string' && record.secure_url) ||
		(typeof record.url === 'string' && record.url) ||
		null;

	if (!url || !isHttpUrl(url)) {
		return null;
	}

	const fileName =
		(typeof record.fileName === 'string' && record.fileName) ||
		(typeof record.original_filename === 'string' && record.original_filename) ||
		fileNameFromUrl(url);

	return { url, fileName };
}

/**
 * Collects one file ref per upload, including multi-file arrays.
 * @param answerValue - Raw or unwrapped file-upload answer
 * @returns File references in stored order
 */
function collectFileRefs(answerValue: unknown): IFileRef[] {
	const value = unwrapAnswerValue(answerValue);
	if (Array.isArray(value)) {
		return value.map(item => extractFileRef(item)).filter((fileRef): fileRef is IFileRef => Boolean(fileRef));
	}

	const fileRef = extractFileRef(value);

	return fileRef ? [fileRef] : [];
}

/**
 * @param questionType - Catalog question type
 * @param customType - Metadata custom type
 * @returns True when the answer should display as a calendar day
 */
function shouldFormatAsDayDate(questionType?: string, customType?: string): boolean {
	return questionType === RCQuestionTypesEnum.BIRTH_DATE || customType === RCCustomQuestionType.DATE;
}

/**
 * @param value - Unwrapped answer
 * @returns True when the signer chose Yes / agreed
 */
function isYesAnswer(value: unknown): boolean {
	const unwrapped = unwrapAnswerValue(value);
	if (unwrapped === true) {
		return true;
	}

	return typeof unwrapped === 'string' && unwrapped.trim().toLowerCase() === 'yes';
}

/**
 * @param value - Unwrapped answer
 * @returns True when the signer chose No
 */
function isNoAnswer(value: unknown): boolean {
	const unwrapped = unwrapAnswerValue(value);
	if (unwrapped === false) {
		return true;
	}

	return typeof unwrapped === 'string' && unwrapped.trim().toLowerCase() === 'no';
}

/**
 * @param questionType - Catalog question type
 * @param customType - Metadata custom type
 * @param metadataText - Snapshotted consent / T&C HTML when present
 * @returns True for waiver / T&C / custom-waiver questions
 */
function isConsentQuestion(questionType?: string, customType?: string, metadataText?: string | null): boolean {
	if (CONSENT_QUESTION_TYPES.has(questionType ?? '') || customType === RCCustomQuestionType.CUSTOM_WAIVER) {
		return true;
	}

	if (customType) {
		return false;
	}

	return questionType === RCQuestionTypesEnum.OTHER && Boolean(metadataText?.trim());
}

/**
 * @param url - File URL
 * @returns True for png/jpeg/webp uploads
 */
function isImageFile(url: string): boolean {
	const match = /\.([a-z0-9]+)(?:\?|#|$)/i.exec(fileNameFromUrl(url));
	const extension = match ? match[1].toLowerCase() : '';
	if (IMAGE_EXTENSIONS.has(extension)) {
		return true;
	}
	if (extension) {
		return false;
	}

	return isAllowedPrintImageUrl(url);
}

/**
 * Mirrors the apiv2 Cloudinary allowlist so print previews cannot load arbitrary image URLs.
 * @param url - Candidate image URL
 * @returns True when the URL is https Cloudinary
 */
function isAllowedPrintImageUrl(url: string): boolean {
	try {
		const parsed = new URL(url);
		if (parsed.protocol !== 'https:') {
			return false;
		}
		if (parsed.username || parsed.password) {
			return false;
		}
		if (/^(?:\d{1,3}\.){3}\d{1,3}$/.test(parsed.hostname)) {
			return false;
		}
		if (isCloudinaryRemoteFetchUrl(parsed)) {
			return false;
		}

		return parsed.hostname === 'res.cloudinary.com' || parsed.hostname.endsWith(ALLOWED_IMAGE_HOST_SUFFIX);
	} catch {
		return false;
	}
}

/**
 * Cloudinary `/image/fetch/` (and overlay `l_fetch:`) would load an arbitrary remote URL.
 * @param parsed - Parsed candidate URL
 * @returns True when the URL is a Cloudinary remote-fetch transform
 */
function isCloudinaryRemoteFetchUrl(parsed: URL): boolean {
	const haystack = decodeUriComponentRepeatedly(`${parsed.pathname}${parsed.search}`);

	return CLOUDINARY_REMOTE_FETCH_PATH.test(haystack) || CLOUDINARY_FETCH_TRANSFORM.test(haystack);
}

/**
 * Node keeps `%2F` encoded in `URL.pathname`. Decode a few times so
 * `/image%2ffetch/` cannot skip the remote-fetch check.
 * @param value - Pathname and search
 * @returns Decoded haystack
 */
function decodeUriComponentRepeatedly(value: string): string {
	let current = value;
	for (let attempt = 0; attempt < 5; attempt += 1) {
		try {
			const decoded = decodeURIComponent(current);
			if (decoded === current) {
				return current;
			}
			current = decoded;
		} catch {
			return current;
		}
	}

	return current;
}

/**
 * Formats a date string without throwing on legacy free-typed values.
 * @param value - Stored date string
 * @returns Formatted day or the original string
 */
function formatDayDate(value: string): string {
	try {
		return formatDateTime(value, DateTimeFormats.DAY_FORMAT);
	} catch {
		return value;
	}
}

/**
 * Formats a questionnaire answer for print and review surfaces.
 * Dates are formatted only for birthDate / custom date questions.
 * File uploads become a filename plus wrapping URL.
 * @param answer - Signed answer DTO or a minimal source object
 * @param metadata - Question metadata and display options
 * @returns Display string
 */
const getAnswerText = (answer: IAnswerTextSource | AnswerDto, metadata?: IAnswerMetadata): string => {
	const customType = metadata?.customType;
	const listSeparator = metadata?.listSeparator ?? DEFAULT_LIST_SEPARATOR;
	const value = unwrapAnswerValue(answer.answerValue);

	if (Array.isArray(value)) {
		return value
			.map(item => getAnswerText({ ...answer, answerValue: item }, metadata))
			.filter(item => item && item !== DASH)
			.join(listSeparator);
	}

	if (isConsentQuestion(answer.questionType, customType, metadata?.consentText)) {
		return isYesAnswer(value) ? AGREED : DASH;
	}

	if (customType === RCCustomQuestionType.YES_NO) {
		if (isYesAnswer(value)) {
			return YES;
		}

		if (isNoAnswer(value)) {
			return NO;
		}

		return DASH;
	}

	if (value === null || value === undefined || value === '') {
		return DASH;
	}

	if (typeof value === 'boolean') {
		return value ? YES : DASH;
	}

	if (customType === RCCustomQuestionType.FILE_UPLOAD) {
		const fileRef = extractFileRef(value);
		if (!fileRef) {
			return DASH;
		}

		return fileRef.fileName && fileRef.fileName !== fileRef.url
			? `${fileRef.fileName}\n${fileRef.url}`
			: fileRef.url;
	}

	if (
		(typeof value === 'string' || typeof value === 'number') &&
		shouldFormatAsDayDate(answer.questionType, customType)
	) {
		return formatDayDate(String(value));
	}

	if (
		answer.questionType === RCQuestionTypesEnum.ADDRESS ||
		(typeof value === 'object' && value !== null && !Array.isArray(value) && 'city' in value)
	) {
		if (typeof value === 'string' || typeof value === 'number') {
			return String(value);
		}

		if (typeof value === 'object' && value !== null) {
			return getAddressString(value as RCAddress) || DASH;
		}
	}

	if (typeof value === 'string' || typeof value === 'number') {
		return String(value);
	}

	try {
		return JSON.stringify(value);
	} catch {
		return DASH;
	}
};

export {
	AGREED,
	DASH,
	NO,
	YES,
	collectFileRefs,
	extractFileRef,
	getAnswerText,
	isAllowedPrintImageUrl,
	isConsentQuestion,
	isImageFile,
	isYesAnswer,
};
